import React from 'react'
import { Button, Section } from 'react-bulma-components'
import { Column, useTable, Cell, Row } from 'react-table'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/solid'

export type ExtendedColumn<D extends object> = Column<D> & {
  className?: string | (() => string)
  isHidden?: (cell: Cell<D>) => boolean
  getRowSpan?: (cell: Cell<D>, data: D[]) => number
}

/** Row shape passed to expand callbacks (react-table row identity + row data). */
export interface ExpandRowContext<ColumnType extends object> {
  original: ColumnType
  index: number
}

interface TableProps<ColumnType extends object> {
  data: any
  columns: ExtendedColumn<ColumnType>[]
  noDataMessage?: string
  className?: string
  tableClassName?: string
  dataTest?: string
  tableRef?: React.RefObject<HTMLTableElement>
  /**
   * When set, prepends a narrow expand column and renders an extra `<tr>` per expanded row.
   * Expanded rows intentionally omit `role="row"` so consumers using `tbody tr[role="row"]` keep stable indices.
   */
  renderExpandedRow?: (row: ExpandRowContext<ColumnType>) => React.ReactNode
  /** If false, the expand cell is empty (no toggle). Defaults to true when `renderExpandedRow` is set. */
  getRowCanExpand?: (row: ExpandRowContext<ColumnType>) => boolean
  /** Stable key for expand state; defaults to `String(index)`. */
  getRowKey?: (row: ExpandRowContext<ColumnType>) => string
}

const Table = <ColumnType extends object>({
  columns,
  data,
  noDataMessage,
  className,
  tableClassName,
  dataTest,
  tableRef,
  renderExpandedRow,
  getRowCanExpand,
  getRowKey,
}: TableProps<ColumnType>): React.ReactElement => {
  const [expandedRowIds, setExpandedRowIds] = React.useState<Set<string>>(() => new Set())
  /** Ref so expand `Cell` reads latest set without recreating column defs every toggle. */
  const expandedRowIdsRef = React.useRef(expandedRowIds)
  expandedRowIdsRef.current = expandedRowIds

  const expandColumn = React.useMemo((): ExtendedColumn<ColumnType> => {
    return {
      id: '__expand__',
      Header: '',
      width: 40,
      Cell: ({ row }: { row: Row<ColumnType> }) => {
        if (!renderExpandedRow) return null
        const ctx: ExpandRowContext<ColumnType> = {
          original: row.original,
          index: row.index,
        }
        const canExpand = getRowCanExpand ? getRowCanExpand(ctx) : true
        if (!canExpand) {
          return null
        }
        const rowKey = getRowKey ? getRowKey(ctx) : String(row.index)
        const isExpanded = expandedRowIdsRef.current.has(rowKey)
        const toggleExpanded = (): void => {
          setExpandedRowIds(prev => {
            const next = new Set(prev)
            if (next.has(rowKey)) {
              next.delete(rowKey)
            } else {
              next.add(rowKey)
            }
            return next
          })
        }
        return (
          <Button
            type="button"
            size="small"
            data-test="expand-row-toggle"
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
            onClick={e => {
              e.stopPropagation()
              toggleExpanded()
            }}
          >
            {isExpanded ? (
              <ChevronUpIcon
                className="icon is-small"
                style={{ width: '1.25rem', height: '1.25rem' }}
              />
            ) : (
              <ChevronDownIcon
                className="icon is-small"
                style={{ width: '1.25rem', height: '1.25rem' }}
              />
            )}
          </Button>
        )
      },
    }
  }, [renderExpandedRow, getRowCanExpand, getRowKey])

  const effectiveColumns = React.useMemo(() => {
    if (!renderExpandedRow) {
      return columns
    }
    return [expandColumn, ...columns]
  }, [columns, expandColumn, renderExpandedRow])

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns: effectiveColumns,
    data,
  })

  const firstHeaderGroup = headerGroups[0]
  const columnCountForColSpan = firstHeaderGroup?.headers.length ?? 0

  return (
    // apply the table props
    <div className={['table-container pb-3', className].join(' ')}>
      <table
        {...getTableProps()}
        className={['table is-striped is-hoverable is-fullwidth is-narrow', tableClassName].join(
          ' ',
        )}
        data-test={dataTest}
        ref={tableRef}
      >
        <thead>
          {
            // Loop over the header rows
            headerGroups.map(headerGroup => (
              // Apply the header row props
              <tr {...headerGroup.getHeaderGroupProps()}>
                {
                  // Loop over the headers in each row
                  headerGroup.headers.map(column => (
                    // Apply the header cell props
                    <th
                      {...column.getHeaderProps({
                        // TS 'any' workaround see react-table issue #1640
                        className: (column as any).className,
                        style: { width: undefined, ...(column as any).style },
                      })}
                    >
                      {
                        // Render the header
                        column.render('Header')
                      }
                    </th>
                  ))
                }
              </tr>
            ))
          }
        </thead>
        {/* Apply the table body props */}
        <tbody {...getTableBodyProps()}>
          {
            // Loop over the table rows
            rows.map(row => {
              // Prepare the row for display
              prepareRow(row)
              const ctx: ExpandRowContext<ColumnType> = {
                original: row.original,
                index: row.index,
              }
              const rowKey = getRowKey ? getRowKey(ctx) : String(row.index)
              const canExpand = renderExpandedRow
                ? getRowCanExpand
                  ? getRowCanExpand(ctx)
                  : true
                : false
              const isExpanded =
                Boolean(renderExpandedRow) && canExpand && expandedRowIds.has(rowKey)
              const rowProps = row.getRowProps({ role: 'row' })

              return (
                <React.Fragment key={rowProps.key as string}>
                  <tr {...rowProps}>
                    {
                      // Loop over the rows cells
                      row.cells.map(cell => {
                        //@ts-ignore
                        const isHidden = cell.column.isHidden && cell.column.isHidden(cell, data)
                        // Apply the cell prop
                        if (isHidden) return null
                        return (
                          <td
                            {...cell.getCellProps({
                              //@ts-ignore
                              rowSpan: cell.column.getRowSpan && cell.column.getRowSpan(cell, data),
                              // TS 'any' workaround see react-table issue #1640
                              className: (cell.column as any).className,
                              style: { width: undefined, ...(row as any).style },
                            })}
                          >
                            {
                              // Render the cell contents
                              cell.render('Cell')
                            }
                          </td>
                        )
                      })
                    }
                  </tr>
                  {isExpanded && renderExpandedRow && (
                    <tr data-test="expanded-row" className="expanded-row">
                      <td colSpan={columnCountForColSpan}>{renderExpandedRow(ctx)}</td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })
          }
        </tbody>
      </table>
      {!rows[0] && (
        <Section textAlign="center">{noDataMessage ?? 'Aucun élément à afficher'}</Section>
      )}
    </div>
  )
}

export default Table
